import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { get } from '../api/apiClient';
import { useAuth } from './AuthContext';

export interface Organization {
  id: string;
  name: string;
  created_at: string;
  current_landscape: string;
  logo?: string;
}

interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  refreshOrganization: () => Promise<Organization | null>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = async () => {
    // Skip fetching if we're already loading to prevent duplicate requests
    if (loading) return organization;
    
    try {
      setLoading(true);
      const data = await get<Organization>('organization');
      setOrganization(data);
      setError(null);
      return data;
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError('Failed to load organization data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch organization data if the user is authenticated
    if (isAuthenticated) {
      fetchOrganization();
    } else {
      // Reset organization data when logged out
      setOrganization(null);
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshOrganization = async () => {
    return await fetchOrganization();
  };

  return (
    <OrganizationContext.Provider value={{ 
      organization, 
      loading, 
      error, 
      refreshOrganization 
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}